/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2021, Bulb Inc.  For more information, please email contact@hellobulb.com.
 *
 *                _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
 *
 */

/**
 * @module image/image/imageloadobserver
 */

import { Observer, ViewRootEditableElement } from 'ckeditor5';

/**
 * Observes all new images added to the {@link module:engine/view/document~Document},
 * fires {@link module:engine/view/document~Document#event:imageLoaded} and
 * {@link module:engine/view/document~Document#event:layoutChanged} event every time when the new image
 * has been loaded.
 *
 * **Note:** This event is not fired for images that have been added to the document and rendered as `complete`
 *     (already loaded).
 *
 * @extends module:engine/view/observer/observer~Observer
 */
export default class BulbImageLoadObserver extends Observer {

  stopObserving(domElement: HTMLElement): void {
    this.stopListening(domElement);
  }

  observedElements : Set<any>;

  constructor(view) {
    super(view);

    /**
     * List of img DOM elements that are observed by this observer.
     *
     * @private
     * @type {Set.<HTMLElement>}
     */
    this.observedElements = new Set();
  }

  /**
   * @inheritDoc
   */
  public override observe(domRoot, name): void {
    const viewRoot = this.document.getRoot(name) as ViewRootEditableElement;

    // When there is a change in one of the view element
    // we need to check if there are any new `<img/>` elements to observe.
    viewRoot.on('change:children', (evt, node) => {
      // Wait for the render to be sure that `<img/>` elements are rendered in the DOM root.
      this.view.once('render', () => this.updateObservedElements(domRoot, node));
    });
  }

  /**
   * Updates the list of observed `<img/>` elements.
   *
   * @private
   * @param {HTMLElement} domRoot DOM root element.
   * @param {module:engine/view/element~ViewElement} viewNode View element where children have changed.
   */
  private updateObservedElements(domRoot, viewNode): void {
    if (!viewNode.is('element') || viewNode.is('attributeElement')) {
      return;
    }
    const domNode = this.view.domConverter.mapViewToDom(viewNode);

    // If there is no `domNode` it means that it was removed from the DOM in the meanwhile.
    if (!domNode) {
      return;
    }

    // Observe all <img> elements and fire events on load
    // @ts-ignore
    for (const domElement of domNode.querySelectorAll('img')) {
      if (!this.observedElements.has(domElement)) {
        this.listenTo(domElement, 'load', (evt, domEvt) => this.fireImgEvents(domEvt));
        this.observedElements.add(domElement);
      }
    }

    // Clean up the list of observed elements from elements that have been removed from the root.
    for (const domElement of this.observedElements) {
      if (!domRoot.contains(domElement)) {
        this.stopListening(domElement);
        this.observedElements.delete(domElement);
      }
    }
  }

  /**
   * Fires {@link module:engine/view/document~Document#event:layoutChanged} and
   * {@link module:engine/view/document~Document#event:imageLoaded}
   * if observer {@link #isEnabled is enabled}.
   *
   * @protected
   * @param {Event} domEvent The DOM event.
   */
  private fireImgEvents(domEvent): void {
    if (this.isEnabled) {
      this.document.fire('layoutChanged');
      this.document.fire('imageLoaded', domEvent);
    }
  }

  /**
   * @inheritDoc
   */
  public override destroy(): void {
    this.observedElements.clear();
    super.destroy();
  }
}
